/**
 * Plan 04-04 (DRC-01) — live, paginated Schedule tab.
 *
 * Backend contract (extended by plan 04-01):
 *   GET /api/v1/adt/procedure_instances
 *     ?workflow_state=Draft,Submitted,InReview,Reopened
 *     &page=1&limit=10&sort=due_date&order=asc
 *   → { data: ProcedureInstance[], meta: { page, limit, total } }
 *
 * The response carries B1 LEFT-JOIN denormalized name columns
 * (program_name / agency_name / lead_name). This component renders them
 * directly — NO client-side fetch of program_instances / agencies / users,
 * NO raw-ID fallback.
 *
 * Component takes ZERO props — call-site cleanup in AuditNetBody.tsx is
 * handled by plan 04-07.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { StatusBadge } from './AuditShared';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import {
    scheduleService,
    ProcedureInstance,
    WORKFLOW_STATES,
    WorkflowState,
} from '../../services/auditService';
import { extractApiError } from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

// Em-dash fallback for null / empty backend-denormalized names (B1 LEFT-JOIN miss).
const display = (s?: string | null) => (s && s.trim().length > 0 ? s : '—');

const AuditSchedule: React.FC = () => {
    const { formatDate } = useEntitySettings();
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [rows, setRows] = useState<ProcedureInstance[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const activeStates = useMemo<WorkflowState[]>(
        () => statusFilter === 'ALL' ? WORKFLOW_STATES : [statusFilter as WorkflowState],
        [statusFilter],
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const res = await scheduleService.list({
                workflow_state: activeStates,
                page,
                limit: pageSize,
            });
            setRows(res.items);
            setTotal(res.total);
        } catch (e: any) {
            setErr(extractApiError(e, 'Failed to load schedule.'));
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [activeStates, page, pageSize]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, pageSize]);

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const daysRemaining = (iso?: string | null): number | null => {
        if (!iso) return null;
        const due = new Date(iso);
        if (isNaN(due.getTime())) return null;
        return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    };

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => setStatusFilter(String(v ?? 'ALL'))}
                        className="filter-status"
                        data-testid="SEL_audit_schedule_status_filter"
                        options={[
                            { value: 'ALL', label: 'All Status' },
                            ...WORKFLOW_STATES.map(s => ({ value: s, label: s })),
                        ]}
                        noSearchIcon
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    {err && (
                        <div className="data-table-empty" role="alert">
                            {err}
                        </div>
                    )}
                    {!err && (
                        <div className="table-list">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th">Program</th>
                                            <th className="table-list-th">Agency</th>
                                            <th className="table-list-th">Type</th>
                                            <th className="table-list-th">Due Date</th>
                                            <th className="table-list-th">Days Remaining</th>
                                            <th className="table-list-th">Lead</th>
                                            <th className="table-list-th table-list-cell--status">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-busy={loading} aria-live="polite">
                                        {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'xs', 'xs', 'sm', 'pill']} />}
                                        {!loading && rows.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="table-list-cell">
                                                    <NoDataFound
                                                        title={statusFilter !== 'ALL' ? 'No Matching Schedules' : 'No Schedules Found'}
                                                        message={statusFilter !== 'ALL' ? 'No procedure instances match the selected status.' : 'No procedure instances scheduled yet.'}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && rows.map((r, idx) => {
                                            const dr = daysRemaining(r.due_date);
                                            return (
                                                <tr key={r.id} className="table-list-row">
                                                    <td className="table-list-cell table-list-cell--sno">
                                                        {(page - 1) * pageSize + idx + 1}
                                                    </td>
                                                    {/* B1 — render backend-denormalized names directly. */}
                                                    <td className="table-list-cell">{display(r.program_name)}</td>
                                                    <td className="table-list-cell">{display(r.agency_name)}</td>
                                                    <td className="table-list-cell">Procedure</td>
                                                    <td className="table-list-cell">
                                                        <span className="inline-flex items-center gap-1">
                                                            <Calendar size={12} />
                                                            {formatDate(r.due_date)}
                                                        </span>
                                                    </td>
                                                    <td
                                                        className={`table-list-cell${dr !== null && dr < 0 ? ' table-list-cell--danger' : ''}`}
                                                    >
                                                        {dr === null
                                                            ? '—'
                                                            : dr < 0
                                                                ? `${Math.abs(dr)} overdue`
                                                                : `${dr}`}
                                                    </td>
                                                    <td className="table-list-cell">{display(r.lead_name)}</td>
                                                    <td className="table-list-cell">
                                                        <StatusBadge status={r.workflow_state} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={total}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                recordLabel="schedules"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditSchedule;
